// src/pages/Users/Vote/UserVotePage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import { getAllVotes, getUserBallots } from "../../../services/voteServices";
import Button from "../../../components/Button/Button";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ParticipateVoteModal from "./ParticipateVoteModal";
import "react-toastify/dist/ReactToastify.css";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { FaVoteYea, FaSearch, FaCalendarAlt, FaSync } from "react-icons/fa";

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  const opts = {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  return date.toLocaleDateString("fr-FR", opts).replace(",", " -");
};

const typeBadge = (type) => {
  switch ((type || "").toLowerCase()) {
    case "sondage":
      return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200";
    case "vote":
    default:
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200";
  }
};

const statusInfo = (v) => {
  const now = new Date();
  const start = v?.start_time ? new Date(v.start_time) : null;
  const end = v?.end_time ? new Date(v.end_time) : null;

  if (start && now < start) {
    return {
      label: "Programmé",
      cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    };
  }
  if (end && now > end) {
    return {
      label: "Clôturé",
      cls: "bg-slate-200 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200",
    };
  }
  if (v?.is_active) {
    return {
      label: "Actif",
      cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    };
  }
  return {
    label: "Inactif",
    cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
  };
};

export default function UserVotePage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;
  const navigate = useNavigate();

  const [votes, setVotes] = useState([]);
  const [ballots, setBallots] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const fetchVotesAndBallots = useCallback(async () => {
    try {
      setLoading(true);
      const [voteData, ballotData] = await Promise.all([
        getAllVotes(),
        getUserBallots(userId),
      ]);

      // Filtrer votes actifs (~fenêtre tolérante comme avant)
      const today = dayjs();
      const activeVotes = (voteData || []).filter((vote) => {
        const start = dayjs(vote.start_time);
        const end = dayjs(vote.end_time);
        return (
          vote.is_active &&
          today.isAfter(start.subtract(1, "day")) &&
          today.isBefore(end.add(1, "day"))
        );
      });

      setVotes(activeVotes);
      setBallots(Array.isArray(ballotData) ? ballotData : []);
    } catch (error) {
      console.error("Erreur chargement votes/réponses:", error);
      toast.error("Erreur lors du chargement des votes ou réponses");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchVotesAndBallots();
  }, [fetchVotesAndBallots]);

  const hasVoted = useCallback(
    (voteId) => ballots.find((b) => b.vote_id === voteId) || null,
    [ballots]
  );

  const filteredVotes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return votes;
    return votes.filter((v) =>
      `${v.title || ""} ${v.description || ""} ${v.type || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [votes, searchTerm]);

  const handleParticipate = (voteId) => {
    setSelectedVoteId(voteId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedVoteId(null);
    setOpenModal(false);
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Utilisateur"
          title="Votes & Sondages"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau gradient */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaVoteYea />
                </div>
                <div>
                  <p className="text-white text-lg sm:text-xl font-semibold">
                    Participe aux votes & sondages du club
                  </p>
                  <p className="text-white/90 text-sm">
                    Réponds pendant la période active. Tes réponses sont
                    enregistrées une seule fois par vote.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="w-full sm:w-auto">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher un vote…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full sm:w-80 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 dark:text-slate-500" />
                  </div>
                </div>

                <Button
                  text="Rafraîchir"
                  icon={FaSync}
                  onClick={fetchVotesAndBallots}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading skeleton */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse h-40 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredVotes.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun vote actif
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Revenez plus tard ou vérifiez si un vote est programmé.
                </p>
              </div>
            )}

            {/* Grille des votes */}
            {!loading && filteredVotes.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVotes.map((v) => {
                  const ballot = hasVoted(v.id);
                  const st = statusInfo(v);

                  return (
                    <div
                      key={v.id}
                      className={`relative rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-5 shadow-sm transition hover:shadow-md ${
                        ballot ? "opacity-85" : ""
                      }`}
                    >
                      {/* Badge “Répondu le …” */}
                      {ballot && (
                        <div className="absolute top-2 right-2 bg-slate-900 text-white text-[11px] px-2 py-1 rounded shadow z-10">
                          Répondu le{" "}
                          {dayjs(ballot.timestamp).format("DD/MM/YYYY")}
                        </div>
                      )}

                      {/* Titre + type */}
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                          {v.title}
                        </h3>
                        <span
                          className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${typeBadge(
                            v.type
                          )}`}
                          title={v.type}
                        >
                          {(v.type || "Vote").charAt(0).toUpperCase() +
                            (v.type || "Vote").slice(1)}
                        </span>
                      </div>

                      {/* Description (2 lignes max) */}
                      {!!v.description && (
                        <p
                          className="mt-1 text-sm text-slate-700/90 dark:text-slate-300/95 line-clamp-2"
                          title={v.description}
                        >
                          {v.description}
                        </p>
                      )}

                      {/* Période + statut */}
                      <div className="mt-3 flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                        <FaCalendarAlt className="opacity-70" />
                        <span className="whitespace-nowrap">
                          {fmtDate(v.start_time)}
                        </span>
                        <span className="opacity-60">→</span>
                        <span className="whitespace-nowrap">
                          {fmtDate(v.end_time)}
                        </span>
                      </div>
                      <div className="mt-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${st.cls}`}
                        >
                          {st.label}
                        </span>
                      </div>

                      {/* CTA */}
                      <div className="mt-4 flex items-center justify-end gap-2">
                        {ballot ? (
                          <>
                            <Button
                              text="Déjà répondu"
                              onClick={() => {}}
                              className="!rounded-xl !px-3 !py-1.5 text-xs sm:text-sm !bg-slate-200 !text-slate-700 dark:!bg-slate-800 dark:!text-slate-200 cursor-not-allowed"
                            />
                          </>
                        ) : (
                          <Button
                            text="Répondre"
                            onClick={() => handleParticipate(v.id)}
                            className="!rounded-xl !px-3 !py-1.5 text-sm"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Toasts */}
        {/* <ToastContainer />  — si déjà global, inutile */}
      </div>

      {/* Modale de participation */}
      {openModal && selectedVoteId && (
        <ParticipateVoteModal
          isOpen={openModal}
          voteId={selectedVoteId}
          userId={userId}
          onClose={handleCloseModal}
          refreshVotes={fetchVotesAndBallots}
        />
      )}
    </section>
  );
}
